import { isDevelopment } from '@app/environment';
import { DocumentTypes } from '@introspection/documentTypes';
import {
  ICardInputType,
  ICardType,
  ICreateCompletionInputType,
  IGraphqlFile,
  IQuery,
  IUpdateCardEventEnumEnum,
  IUserType,
} from '@introspection/index';
import { Inject, Injectable } from '@rxdi/core';
import { DataProxy, deepCopy, importQuery } from '@rxdi/graphql-client';
import firebase from 'firebase/app';
import { DocumentNode } from 'graphql';
import { from, iif, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { BaseGraphqlService } from '../base';
import { UserService } from '../user/user.service';
import { addCardToState, modCard, removeCardFromState } from './card.helpers';

@Injectable()
export class CardService extends BaseGraphqlService {
  @Inject(UserService)
  private userService: UserService;

  private $updating: boolean;

  shouldUpdate() {
    return this.$updating;
  }

  set updating(value: boolean) {
    this.$updating = value;
  }

  moveCard({
    prevBoardId,
    columnId,
    boardId,
    cardId,
    prevColumnId,
    order,
  }: {
    cardId: string;
    columnId: string;
    boardId: string;
    prevBoardId?: string;
    prevColumnId: string;
    order: number;
  }) {
    return this.mutate({
      mutation: 'move-card.graphql',
      variables: {
        id: cardId,
        columnId,
        order,
      },
      context: {
        requestTrackerId: 1,
      },
      update: (proxy, { data: { moveCard } }) => {
        const newBoard = this._getState(proxy, boardId);
        if (newBoard) {
          this._writeState({
            ...newBoard,
            getBoard: addCardToState(boardId, columnId)(moveCard)(
              removeCardFromState(boardId, prevColumnId === columnId ? columnId : prevColumnId)(moveCard)(
                newBoard.getBoard
              )
            ),
          })(proxy, boardId);
        }

        const prevBoard = this._getState(proxy, prevBoardId);
        if (prevBoard) {
          this._writeState({
            ...prevBoard,
            getBoard: removeCardFromState(prevBoardId, prevColumnId)(moveCard)(prevBoard.getBoard),
          })(proxy, prevBoardId);
        }
      },
    }).pipe(map((v) => v.data.moveCard));
  }

  addMeToMembers(card: ICardType, event: IUpdateCardEventEnumEnum) {
    return this.userService.getUser().pipe(
      switchMap((u) =>
        this.updateCard(
          card.id,
          {
            ...card,
            members: [...card.members, u].map((m) => ({
              id: m.id,
            })),
          },
          event
        )
      )
    );
  }

  removeMefromMembers(card: ICardType, event: IUpdateCardEventEnumEnum) {
    return this.userService.getUser().pipe(
      switchMap((u) =>
        this.updateCard(
          card.id,
          {
            ...card,
            members: card.members.filter((m) => m.id !== u.id).map((m) => ({ id: m.id })),
          },
          event
        )
      )
    );
  }

  isCardMember(userId: string) {
    return (card: ICardType) => !!card.members.find((m) => m.id === userId);
  }

  onAddMeToCardMembers(card: ICardType, event: IUpdateCardEventEnumEnum) {
    return this.userService
      .getUser()
      .pipe(
        switchMap((user) =>
          iif(
            () => !!this.isCardMember(user.id)(card),
            this.removeMefromMembers(card, event),
            this.addMeToMembers(card, event)
          )
        )
      );
  }

  addCardMembers(card: ICardType, members: IUserType[], event: IUpdateCardEventEnumEnum) {
    return this.updateCard(
      card.id,
      {
        ...card,
        members,
      },
      event
    );
  }

  removeCardAttachment(id: string) {
    return (cardId: string) =>
      this.getCard(cardId).pipe(
        switchMap((card) =>
          this.mutate({
            mutation: 'remove-card-attachment.graphql',
            variables: {
              id,
            },
            update: (proxy) => {
              const data = this._getState(proxy, card.board.id);
              const getBoard = modCard(
                card.board.id,
                card.column.id,
                card.id
              )((card) => {
                return {
                  ...card,
                  files: card.files?.filter((file) => file.id !== id),
                };
              })(data.getBoard);
              this._writeState({
                ...data,
                getBoard,
              })(proxy, card.board.id);
            },
          }).pipe(map((res) => res.data.removeCardAttachment))
        )
      );
  }

  uploadAttachment(cardId: string, files: File[]): Observable<IGraphqlFile[]> {
    if (!files.length) {
      return of([]);
    }
    const body = new FormData();
    for (const file of files) {
      body.append('file[]', file);
    }

    return this.getCard(cardId).pipe(
      switchMap((card) => {
        return from(firebase.auth().currentUser.getIdToken()).pipe(
          switchMap((Authorization) =>
            fetch(isDevelopment() ? 'http://localhost:9000/upload' : 'https://pubsub.graphql-server.com/upload', {
              method: 'POST',
              body,
              headers: {
                Authorization,
                cardId,
              },
            })
          ),
          switchMap((file) => file.json()),
          map((data) => {
            if (data.error) {
              console.error(data);
              return [] as IGraphqlFile[];
            }
            try {
              const state = deepCopy(
                this.graphql.readQuery<IQuery>({
                  query: this._getQuery(),
                  variables: { id: card.board.id },
                })
              );
              const getBoard = modCard(
                card.board.id,
                card.column.id,
                card.id
              )((card) => ({
                ...card,
                files: [...(card.files || []), ...data],
              }))(state.getBoard);
              this.graphql.writeQuery<IQuery>({
                query: this._getQuery(),
                variables: { id: card.board.id },
                data: {
                  ...state,
                  getBoard,
                },
              });
            } catch (e) {
              console.log(e);
              console.error('State not updated for uploading attachment');
            }

            return data;
          })
        );
      })
    );
  }

  getCard(id: string) {
    return this.query({
      query: 'get-card.graphql',
      variables: {
        id,
      },
    }).pipe(map(({ data }) => data.getCard));
  }
  watchCard(id: string) {
    return this.watchQuery({
      query: 'get-card.graphql',
      variables: {
        id,
      },
    }).pipe(map(({ data }) => data.getCard));
  }

  cardChanged(cardId: string) {
    return this.subscribe({
      query: 'card-changed.subscription.graphql',
      variables: {
        cardId,
      },
    }).pipe(map(({ data }) => data.cardChanged));
  }

  updateCard(id: string, data: ICardInputType, event: IUpdateCardEventEnumEnum) {
    return this.mutate({
      mutation: 'update-card.graphql',
      variables: {
        id,
        payload: {
          members: data.members?.map(({ id }) => ({ id })),
          title: data.title,
          description: data.description,
          dueDate: data.dueDate,
          order: data.order,
          dueDateComplete: data.dueDateComplete,
          labels: data.labels,
          pullRequest: data.pullRequest,
        },
        event,
      },
      //  context: {
      //   requestTrackerId: 1,
      //  },
    }).pipe(map(({ data }) => data.updateCard));
  }

  addCard(boardId: string, columnId: string, payload: ICardInputType) {
    return this.mutate({
      mutation: 'add-card.graphql',
      variables: {
        boardId,
        columnId,
        payload,
      },
      update: (proxy, { data: { addCard } }) => {
        const data = this._getState(proxy, boardId);

        this._writeState({
          ...data,
          getBoard: addCardToState(boardId, columnId)(addCard)(data.getBoard),
        })(proxy, boardId);
      },
    }).pipe(map(({ data }) => data.addCard));
  }

  addComment(cardId: string, value: string, mentions: string[]) {
    return this.mutate({
      mutation: 'add-card-comment.graphql',
      variables: {
        cardId,
        value,
        mentions,
      },
      update: (proxy, { data: { addCardComment } }) => {
        const state = deepCopy(
          this.graphql.readQuery<IQuery>({
            query: this._getQuery(),
            variables: { id: addCardComment.boardId },
          })
        );
        if (!state) {
          return;
        }
        const getBoard = modCard(
          addCardComment.boardId,
          addCardComment.column.id,
          addCardComment.id
        )(() => addCardComment)(state.getBoard);
        this.graphql.writeQuery<IQuery>({
          query: this._getQuery(),
          variables: { id: addCardComment.boardId },
          data: {
            ...state,
            getBoard,
          },
        });
      },
    }).pipe(map(({ data }) => data.addCard));
  }

  deleteComment(cardId: string, commentId: string) {
    return this.mutate({
      mutation: 'delete-card-comment.graphql',
      variables: {
        cardId,
        commentId,
      },
      update: (proxy, { data: { deleteCardComment } }) => {
        const state = deepCopy(
          this.graphql.readQuery<IQuery>({
            query: this._getQuery(),
            variables: { id: deleteCardComment.boardId },
          })
        );
        if (!state) {
          return;
        }

        this.graphql.writeQuery<IQuery>({
          query: this._getQuery(),
          variables: { id: deleteCardComment.boardId },
          data: {
            ...state,
            getBoard: addCardToState(deleteCardComment.boardId, deleteCardComment.column.id)(deleteCardComment)(
              removeCardFromState(deleteCardComment.boardId, deleteCardComment.column.id)(deleteCardComment)(
                state.getBoard
              )
            ),
          },
        });
      },
    }).pipe(map(({ data }) => data.addCard));
  }

  updateComment(cardId: string, commentId: string, value: string) {
    return this.mutate({
      mutation: 'update-card-comment.graphql',
      variables: {
        cardId,
        commentId,
        value,
      },
      update: (proxy, { data: { updateCardComment } }) => {
        const state = this.graphql.readQuery<IQuery>({
          query: this._getQuery(),
          variables: { id: updateCardComment.boardId },
        });
        if (!state) {
          return;
        }
        this.graphql.writeQuery<IQuery>({
          query: this._getQuery(),
          variables: { id: updateCardComment.boardId },
          data: {
            ...state,
            getBoard: addCardToState(updateCardComment.boardId, updateCardComment.column.id)(updateCardComment)(
              removeCardFromState(updateCardComment.boardId, updateCardComment.column.id)(updateCardComment)(
                state.getBoard
              )
            ),
          },
        });
      },
    }).pipe(map(({ data }) => data.updateCardComment));
  }

  createCompletion(payload: ICreateCompletionInputType) {
    return this.mutate({ mutation: 'create-completion.graphql', variables: { payload } }).pipe(
      map((res) => res.data.createCompletion)
    );
  }

  createCompletionGemini(payload: ICreateCompletionInputType) {
    return this.mutate({ mutation: 'create-completion-gemini.graphql', variables: { payload } }).pipe(
      map((res) => res.data.createCompletionGemini)
    );
  }

  private _getQuery(): DocumentNode {
    return importQuery<DocumentTypes>('get-board.query.graphql');
  }

  private _getState = (proxy: DataProxy, id: string) =>
    deepCopy(
      proxy.readQuery<IQuery>({
        query: this._getQuery(),
        variables: { id },
      })
    );

  private _writeState = (data: IQuery) => (proxy: DataProxy, id: string) =>
    proxy.writeQuery<IQuery>({
      query: this._getQuery(),
      variables: {
        id,
      },
      data,
    });
}
